import { useMemo } from 'react';
import {
  type GeoPoint,
  GoogleMarkerMapPicker,
} from '@/routers/inventorization/components/position/GoogleMarkerMapPicker';
import { config } from '@/shared/config/index';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';

interface GpsPositionMapProps {
  currentPosition: GeoPoint | null;
  focusPoint: GeoPoint | null;
  mapCenter: GeoPoint;
  onPick: (point: GeoPoint) => void;
  otherMeterPins: GeoPoint[];
  selectedPin: GeoPoint | null;
}

export function GpsPositionMap({
  currentPosition,
  focusPoint,
  mapCenter,
  onPick,
  otherMeterPins,
  selectedPin,
}: GpsPositionMapProps) {
  const mapOptions = useMemo<google.maps.MapOptions>(
    () => ({
      center: mapCenter,
      clickableIcons: false,
      disableDoubleClickZoom: true,
      fullscreenControl: false,
      gestureHandling: 'greedy',
      mapTypeControl: true,
      streetViewControl: false,
      tilt: 0,
      zoom: 19,
      zoomControl: true,
    }),
    [mapCenter],
  );
  const referenceMarkers = useMemo(
    () => [
      ...otherMeterPins.map(pin => ({
        color: '#595959',
        point: pin,
        scale: 6,
        shape: 'pin' as const,
        title: pin.key
          ? translateInventoryText('app.inventory.position.map.meterTitle', 'Meter {key}', { key: pin.key })
          : translateInventoryText('app.inventory.position.map.nearbyMeter', 'Nearby meter'),
        zIndex: 10,
      })),
      ...(currentPosition
        ? [
            {
              color: '#1677ff',
              point: currentPosition,
              scale: 9,
              title: translateInventoryText('app.inventory.position.map.currentGps', 'Current GPS'),
              zIndex: 20,
            },
          ]
        : []),
    ],
    [currentPosition, otherMeterPins],
  );
  const selectedGoogleMarker = useMemo(
    () =>
      selectedPin
        ? {
            color: '#fa541c',
            label: 'M',
            point: selectedPin,
            title: translateInventoryText('app.inventory.position.map.thisMeter', 'This meter'),
          }
        : null,
    [selectedPin],
  );

  return (
    <GoogleMarkerMapPicker
      apiKey={config.googleMapsApiKey}
      failedDescription={translateInventoryText(
        'app.inventory.position.map.googleFailed',
        'Google Maps could not be loaded.',
      )}
      focusPoint={focusPoint}
      height={360}
      loadingDescription={translateInventoryText('app.inventory.position.map.googleLoading', 'Loading Google Maps...')}
      manualDescription={translateInventoryText(
        'app.inventory.position.map.manualCoordinatesAvailable',
        'Manual latitude and longitude inputs remain available.',
      )}
      mapOptions={mapOptions}
      onPick={onPick}
      referenceMarkers={referenceMarkers}
      selectedMarker={selectedGoogleMarker}
    />
  );
}
